<template>
  <div
    class="inline-flex items-center gap-3 rounded-md px-3 py-2"
    :style="{
      background: 'var(--bg-card)',
      border: `1px solid var(${borderVar})`,
    }"
    :data-test-rate="rate ?? ''"
    data-test="predictability-readout"
  >
    <div class="flex flex-col">
      <span class="text-[11px] uppercase tracking-wider" style="color: var(--fg-subtle)">
        Predictability
      </span>
      <span class="text-xl font-semibold leading-tight" :style="{ color: `var(${valueColorVar})` }">
        {{ percentLabel }}
      </span>
    </div>
    <div class="text-xs leading-tight" style="color: var(--fg-muted)">
      <p>{{ qualifyingLabel }}</p>
      <p class="mt-0.5">within ±{{ tolerancePctLabel }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    rate: number | null;
    withinCount: number;
    qualifyingCount: number;
    tolerance?: number; // 0..1
  }>(),
  { tolerance: 0.10 },
);

const percentLabel = computed(() => {
  if (props.rate == null) return "—";
  return `${Math.round(props.rate * 100)}%`;
});

const qualifyingLabel = computed(() => {
  if (props.qualifyingCount === 0) return "no qualifying sprints";
  return `${props.withinCount} of ${props.qualifyingCount} sprint${props.qualifyingCount === 1 ? "" : "s"}`;
});

const tolerancePctLabel = computed(() => `${Math.round(props.tolerance * 100)}%`);

// Band: rate >= 0.7 green, >= 0.5 amber, < 0.5 red, null = none
const band = computed<"green" | "amber" | "red" | "none">(() => {
  if (props.rate == null) return "none";
  if (props.rate >= 0.7) return "green";
  if (props.rate >= 0.5) return "amber";
  return "red";
});

const borderVar = computed(() => {
  switch (band.value) {
    case "green": return "--rag-green";
    case "amber": return "--rag-amber";
    case "red":   return "--rag-red";
    default:      return "--border";
  }
});

const valueColorVar = computed(() => {
  switch (band.value) {
    case "green": return "--rag-green";
    case "amber": return "--rag-amber";
    case "red":   return "--rag-red";
    default:      return "--fg";
  }
});

defineExpose({ band });
</script>
